import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'TR' | 'EN';

export function getLocalizedPath(path: string, targetLang: Language): string {
  const cleanPath = path.split('?')[0].split('#')[0];
  const queryAndHash = path.slice(cleanPath.length);

  if (targetLang === 'EN') {
    if (cleanPath === '/' || cleanPath === '') return `/en${queryAndHash}`;
    if (cleanPath === '/en' || cleanPath.startsWith('/en/')) return path;
    if (cleanPath === '/projeler') return `/en/projects${queryAndHash}`;
    if (cleanPath.startsWith('/projeler/')) return `/en/projects${cleanPath.slice('/projeler'.length)}${queryAndHash}`;
    if (cleanPath === '/hizmetler') return `/en/services${queryAndHash}`;
    if (cleanPath.startsWith('/hizmetler/')) return `/en/services${cleanPath.slice('/hizmetler'.length)}${queryAndHash}`;
    if (cleanPath === '/hakkimizda') return `/en/about${queryAndHash}`;
    if (cleanPath === '/markalar') return `/en/brands${queryAndHash}`;
    if (cleanPath === '/iletisim') return `/en/contact${queryAndHash}`;
    if (cleanPath === '/gizlilik') return `/en/privacy${queryAndHash}`;
    if (cleanPath === '/kvkk') return `/en/kvkk${queryAndHash}`;
    if (cleanPath === '/cerez-politikasi') return `/en/cookie-policy${queryAndHash}`;
    return `/en${cleanPath}${queryAndHash}`;
  } else {
    // Target TR
    if (cleanPath === '/en' || cleanPath === '/en/') return `/${queryAndHash}`;
    if (!cleanPath.startsWith('/en')) return path;
    if (cleanPath === '/en/projects') return `/projeler${queryAndHash}`;
    if (cleanPath.startsWith('/en/projects/')) return `/projeler${cleanPath.slice('/en/projects'.length)}${queryAndHash}`;
    if (cleanPath === '/en/services') return `/hizmetler${queryAndHash}`;
    if (cleanPath.startsWith('/en/services/')) return `/hizmetler${cleanPath.slice('/en/services'.length)}${queryAndHash}`;
    if (cleanPath === '/en/about') return `/hakkimizda${queryAndHash}`;
    if (cleanPath === '/en/brands') return `/markalar${queryAndHash}`;
    if (cleanPath === '/en/contact') return `/iletisim${queryAndHash}`;
    if (cleanPath === '/en/privacy') return `/gizlilik${queryAndHash}`;
    if (cleanPath === '/en/kvkk') return `/kvkk${queryAndHash}`;
    if (cleanPath === '/en/cookie-policy') return `/cerez-politikasi${queryAndHash}`;
    return `${cleanPath.replace(/^\/en/, '') || '/'}${queryAndHash}`;
  }
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  getLocalizedPath: (path: string, targetLang: Language) => string;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      if (pathname === '/en' || pathname.startsWith('/en/')) {
        return 'EN';
      }
      const saved = localStorage.getItem('we24_lang');
      if (saved === 'EN' || saved === 'TR') return saved;
    }
    return 'TR';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    if (typeof window !== 'undefined') {
      localStorage.setItem('we24_lang', lang);
      document.documentElement.lang = lang.toLowerCase();
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.documentElement.lang = language.toLowerCase();
    }
  }, [language]);

  const t = (key: string): string => key;

  return (
    <LanguageContext.Provider value={{ language, setLanguage, getLocalizedPath, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
